import { createFileRoute, notFound } from "@tanstack/react-router"
import Store from "@/pages/store"
import type { HttpTypes } from "@medusajs/types"
import { sanitize } from "@/lib/utils/sanitize"
import { PREORDER_ENABLED, SITE_NAME } from "@/lib/constants/site"
import { SITE_URL } from "@/lib/constants/site-url"
import { filterStoreCatalogProducts } from "@/lib/utils/catalog-visibility"
import { normalizeStoreSearchQuery } from "@/lib/utils/store-search"
import { jsonLd } from "@/lib/utils/json-ld"

const OG_IMAGE = `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`

export const Route = createFileRoute("/$countryCode/store")({
  validateSearch: (search): { q?: string } => {
    const q = normalizeStoreSearchQuery(search.q)
    return q ? { q } : {}
  },
  loader: async ({ params, context }) => {
    if (!PREORDER_ENABLED) {
      throw notFound()
    }
    const { countryCode } = params
    const { queryClient } = context
    const [
      { getRegion },
      { listProducts },
      { retrieveCategoryTreeIds, STORE_ROOT_CATEGORY_HANDLE },
    ] = await Promise.all([
      import("@/lib/data/regions"),
      import("@/lib/data/products"),
      import("@/lib/data/categories"),
    ])

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    let storeCategoryIds: string[] = []
    try {
      storeCategoryIds = await queryClient.ensureQueryData({
        queryKey: ["category-tree-ids", STORE_ROOT_CATEGORY_HANDLE],
        queryFn: () =>
          retrieveCategoryTreeIds({ handle: STORE_ROOT_CATEGORY_HANDLE }),
      })
    } catch (error) {
      console.warn("[+Bake Cafe] Failed to resolve store category tree", error)
    }

    let products: HttpTypes.StoreProduct[] = []
    try {
      const productResponse = await queryClient.ensureQueryData({
        queryKey: [
          "products",
          { region_id: region.id, category_id: storeCategoryIds },
        ],
        queryFn: () => listProducts({
          query_params: {
            ...(storeCategoryIds.length > 0
              ? { category_id: storeCategoryIds }
              : {}),
            limit: 100, // Reduce limit for SSR performance
            order: "-created_at"
          },
          region_id: region.id,
        }),
      })
      products = filterStoreCatalogProducts(
        productResponse.products as HttpTypes.StoreProduct[],
      )
    } catch (error) {
      console.warn("[+Bake Cafe] Failed to preload products for store", error)
    }

    return sanitize({
      countryCode,
      region,
      storeCategoryIds,
      products,
    })
  },
  head: ({ loaderData }) => {
    if (!PREORDER_ENABLED) {
      return {}
    }
    const { region, countryCode, products } = loaderData || {}
    const regionName = region?.name || countryCode?.toUpperCase()
    const title = regionName
      ? `Pre-Order Bakery & Coffee Pickup - ${regionName} | ${SITE_NAME}`
      : `Pre-Order Bakery & Coffee Pickup | ${SITE_NAME}`
    const description = `Pre-order +Bake Cafe pastries, savory bites, and specialty lattes for local pickup in Rancho Cucamonga.`
    const canonical = `${SITE_URL}/store`

    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${SITE_NAME} pre-order catalog`,
      itemListElement: (products ?? []).slice(0, 50).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: product.title,
        url: `${SITE_URL}/products/${product.handle}`,
      })),
    }

    const collectionSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description,
      url: canonical,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:site_name", content: SITE_NAME },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(collectionSchema) },
        { type: "application/ld+json", children: jsonLd(itemListSchema) },
      ],
    }
  },
  component: Store,
})
